import { QMulti } from '@educraft/base';
import { PdfGen, PdfUnit, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { ILayoutMultiGen } from '../layoutMulti';
import { OptsMulti } from '../optsMulti';
import { GUtil } from '../../pdf/gutil';
import { genMiniLuk, drawMiniLuk, MiniLukData } from '../../pdf/board/miniLuk';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';

import { IItemCount } from '@educraft/base';

/**
 * MiniLUK (Multi version)
 *
 * Layout: Landscape A4 with MiniLUK puzzle format.
 * Multiple choice questions grouped for the MiniLUK matching puzzle.
 *
 * Migrated from C# EduFrame.Multi.Generators.MiniLUK
 */
export class MiniLUK implements ILayoutMultiGen, IItemCount {
  countDefault(): number {
    return 1;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const totalPuzzles = this.countDefault();

    for (let pIdx = 0; pIdx < totalPuzzles; pIdx++) {
      const page = pdf.addPage(true); // Landscape

      const margin1 = PdfUnit.FromMillimeters(6);
      const margin2 = PdfUnit.FromMillimeters(3);

      // Generate items using MiniLUK grouping
      const items = genMiniLuk<QMulti>(
        () => opts.generator.generate(opts.data),
        (item) => item.answerIndex.toString(),
        (item) => `${item.texts.join('|')  }|${  item.answers.join('|')}`,
        new Set([12, 6, 4, 3, 2]),
        opts.data.random
      );

      let font = GUtil.baseFont;

      drawMiniLuk(
        page,
        items,
        // Draw question with multiple choice answers
        (data: MiniLukData<QMulti>) => {
          const texts: string[] = [];
          texts.push(...data.obj.texts);
          for (let index = 0; index < data.obj.answers.length; index++) {
            texts.push(`${letter(index)}) ${data.obj.answers[index]}`);
          }

          const rect = new PdfRect(
            data.rect.x,
            data.rect.y + margin1,
            data.rect.width,
            data.rect.height - margin1 - margin2
          );
          page.drawStrings(texts, font, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);
        },
        // Draw answer (just the letter)
        (data: MiniLukData<QMulti>) => {
          page.drawFillStringCenter(
            letter(data.obj.answerIndex),
            GUtil.baseFont,
            PdfBrush.Black,
            data.rect
          );
        },
        // Width calculated callback for font adjustment
        (width: number) => {
          const allTexts = items.flatMap((i) => [
            ...i.obj.texts,
            ...i.obj.answers.map((a, idx) => `${letter(idx)}) ${a}`),
          ]);
          font = page.adjustFont(width, allTexts, font);
        },
        opts.data
      );
    }

    function letter(index: number): string {
      return String.fromCharCode('A'.charCodeAt(0) + index);
    }
  }
}
